import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

interface TimelineControlProps {
  startDate: Date;
  endDate: Date;
  selectedRange: [Date | null, Date | null];
  onRangeChange: (range: [Date | null, Date | null]) => void;
}

export const TimelineControl: React.FC<TimelineControlProps> = ({
  startDate,
  endDate,
  selectedRange,
  onRangeChange
}) => {
  const [rangeStart, rangeEnd] = selectedRange;
  const totalDuration = endDate.getTime() - startDate.getTime();
  
  // Calculate positions as percentages
  const getPositionPercent = (date: Date | null) => {
    if (!date) return 0;
    return ((date.getTime() - startDate.getTime()) / totalDuration) * 100;
  };
  
  const startPercent = getPositionPercent(rangeStart);
  const endPercent = getPositionPercent(rangeEnd);
  
  // Handle drag operations
  const [isDragging, setIsDragging] = useState<'start' | 'end' | 'range' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [initialStartPercent, setInitialStartPercent] = useState(0);
  const [initialEndPercent, setInitialEndPercent] = useState(100);
  
  const handleMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'range') => {
    setIsDragging(type);
    setDragStartX(e.clientX);
    setInitialStartPercent(startPercent);
    setInitialEndPercent(endPercent);
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const timelineEl = document.getElementById('timeline-control');
    if (!timelineEl) return;
    
    const rect = timelineEl.getBoundingClientRect();
    const width = rect.width;
    const deltaX = e.clientX - dragStartX;
    const deltaPercent = (deltaX / width) * 100;
    
    if (isDragging === 'start') {
      let newStartPercent = Math.max(0, Math.min(initialStartPercent + deltaPercent, initialEndPercent - 5));
      const newStartDate = new Date(startDate.getTime() + (totalDuration * newStartPercent / 100));
      onRangeChange([newStartDate, rangeEnd]);
    } else if (isDragging === 'end') {
      let newEndPercent = Math.min(100, Math.max(initialEndPercent + deltaPercent, initialStartPercent + 5));
      const newEndDate = new Date(startDate.getTime() + (totalDuration * newEndPercent / 100));
      onRangeChange([rangeStart, newEndDate]);
    } else if (isDragging === 'range') {
      const rangeDuration = initialEndPercent - initialStartPercent;
      let newStartPercent = Math.max(0, Math.min(initialStartPercent + deltaPercent, 100 - rangeDuration));
      let newEndPercent = newStartPercent + rangeDuration;
      
      const newStartDate = new Date(startDate.getTime() + (totalDuration * newStartPercent / 100));
      const newEndDate = new Date(startDate.getTime() + (totalDuration * newEndPercent / 100));
      onRangeChange([newStartDate, newEndDate]);
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(null);
  };
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);
  
  // Format dates for display
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy HH:mm');
  };
  
  return (
    <div className="timeline-control" id="timeline-control">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <div>{formatDate(startDate)}</div>
        <div>{formatDate(endDate)}</div>
      </div>
      
      <div className="relative h-8 bg-gray-200 rounded-lg">
        {/* Timeline markers */}
        <div className="absolute inset-y-0 left-0 w-full flex">
          {Array.from({ length: 10 }).map((_, i) => (
            <div 
              key={i} 
              className="flex-1 border-l border-gray-300 h-full first:border-l-0"
            />
          ))}
        </div>
        
        {/* Selected range */}
        <div 
          className="absolute inset-y-0 bg-blue-200 cursor-move"
          style={{ 
            left: `${startPercent}%`, 
            width: `${endPercent - startPercent}%` 
          }}
          onMouseDown={(e) => handleMouseDown(e, 'range')}
        />
        
        {/* Start handle */}
        <div 
          className="absolute top-0 bottom-0 w-3 bg-blue-500 rounded-l-lg cursor-ew-resize"
          style={{ left: `${startPercent}%`, marginLeft: '-1.5px' }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
        />
        
        {/* End handle */}
        <div 
          className="absolute top-0 bottom-0 w-3 bg-blue-500 rounded-r-lg cursor-ew-resize"
          style={{ left: `${endPercent}%`, marginLeft: '-1.5px' }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
        />
      </div>
      
      <div className="flex justify-between mt-1">
        <div className="text-xs font-medium">{formatDate(rangeStart)}</div>
        <div className="text-xs font-medium">{formatDate(rangeEnd)}</div>
      </div>
    </div>
  );
};
